// assetDownloadRules — what "download this asset's file" resolves to, as pure
// decisions. Leaf module (no CustomAssetManager, no import.meta.env) so it is
// testable under plain `node --test`, the same split ogsAssetRecord.js makes.
//
// The one thing worth knowing here: an asset row's `url` is the URL the SERVER
// stated (`file_url` / `fileUrl`), and for the /ogs collection that is an
// ASSET-MANAGER-relative path — `/api/characters/<slug>/ogs/<file>`. It is not
// page-relative: there is no Vite proxy entry for it, so fetching it from the
// page falls through the generic `/api` rule to convorcher, which answers a
// phantom 404 for a file the AAM serves fine (the trap vite.config.js already
// records for org environment files). So a relative url is marked `viaAam` and
// the caller must fetch it through assetManagerApi.fetchAamFile, which prefixes
// the AAM base and carries the auth header — exactly what ogsContentPacks does
// with these same rows.
//
// Nothing here builds or derives a URL. It only classifies the one the row
// states, and rewrites a raw storage host to the credentialed proxy path via
// toProxiedAssetUrl (which the rig download already relies on: the raw S3
// object 403s).

import { toProxiedAssetUrl } from './characterAssetUrl.js'

// The kinds whose rows stand for a FILE on the server. A material, gesture,
// prefab or blueprint row is a document the server composes — there is no
// object to hand over — so those never offer the control at all, rather than
// offering it permanently disabled (blueprints have their own per-card
// "Export to file…", which serialises a graph instead of fetching one).
const FILE_BACKED_KINDS = new Set([
  'ogs', 'body_anim', 'face_anim', 'audio', 'texture', 'environment', 'rig',
])

/** Does this kind of asset stand for a downloadable file on the server? */
export function isFileBackedKind(kind) {
  return FILE_BACKED_KINDS.has(kind)
}

/**
 * Resolve `{ url, filename, viaAam }` for downloading an asset's underlying
 * file, or null when the row states no file URL.
 *
 * `viaAam` true  → a path relative to the asset-manager; fetch with fetchAamFile.
 * `viaAam` false → an absolute URL on some other host; fetch it directly.
 */
export function assetDownloadTarget(asset) {
  const raw = typeof asset?.url === 'string' ? asset.url.trim() : ''
  if (!raw) return null
  const url = toProxiedAssetUrl(raw)
  const absolute = /^[a-z][a-z0-9+.-]*:/i.test(url)
  return { url, filename: downloadFileName(asset, url), viaAam: !absolute }
}

/**
 * The name the saved file gets.
 *
 * Preference order, and each step exists for a real row shape:
 *   1. `rawId`, when it already IS a filename — that is exactly what an /ogs
 *      row's rawId is (the collection is keyed by bare filename).
 *   2. the asset's own NAME plus the extension the stored file has. The card
 *      you right-clicked says "responding_face"; the object behind it is stored
 *      as `.../<uuid>/face.json`, so saving the url's tail hands over a
 *      "face.json" that is indistinguishable from every other face clip. The
 *      name is what the user picked it by, so it is what the file is called.
 *   3. the url's last segment, when the row has no name at all.
 */
export function downloadFileName(asset, url = asset?.url) {
  const rawId = str(asset?.rawId)
  if (looksLikeFileName(rawId)) return rawId

  const fromUrl = lastPathSegment(url)
  const name = safeFileName(str(asset?.name))
  if (name) {
    const ext = extensionOf(fromUrl) || extensionOf(rawId)
    return ext && !name.toLowerCase().endsWith(ext.toLowerCase()) ? name + ext : name
  }
  if (looksLikeFileName(fromUrl)) return fromUrl
  return fromUrl || rawId || 'download'
}

/**
 * Why the download control is unavailable for this asset. The control is SHOWN
 * AND DISABLED with this inline, never hidden — the rule voiceInputState.js's
 * header sets out, and the same shape as NO_RIG_REASON.
 */
export function downloadUnavailableReason(asset) {
  if (!asset) return 'No asset selected.'
  return `"${str(asset.name) || str(asset.rawId) || 'This asset'}" has no file on the `
    + 'server — there is nothing to download.'
}

/**
 * The progress line for an in-flight download.
 *
 * A percentage needs a total, and a total is only there when the server sent a
 * Content-Length — which it does not when the body is compressed at the edge.
 * So the byte count is the fallback rather than a fake percentage: a progress
 * bar that invents its own denominator is worse than one that counts up.
 */
export function downloadProgressText(received, total, label = '') {
  const what = label ? ` ${label}` : ''
  if (Number.isFinite(total) && total > 0 && Number.isFinite(received)) {
    const pct = Math.max(0, Math.min(100, Math.round((received / total) * 100)))
    return `Downloading${what}… ${pct}%`
  }
  if (Number.isFinite(received) && received > 0) {
    return `Downloading${what}… ${formatBytes(received)}`
  }
  return `Downloading${what}…`
}

/**
 * Human byte size for the progress line. One decimal above a kilobyte, which is
 * the Inspector's own `fmtBytes` shape — the number beside it in the OGS panel
 * is that formatter's, and two formats for the same file in one panel reads as
 * a bug. (uploadLimits.js has a third, deliberately different: it states a
 * LIMIT, where "2 GB" is the number people quote back at you.)
 */
export function formatBytes(n) {
  if (!Number.isFinite(n) || n < 0) return '—'
  if (n < 1024) return `${n} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let v = n / 1024
  let i = 0
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++ }
  return `${v.toFixed(1)} ${units[i]}`
}

// Path separators, Windows-reserved punctuation, and control characters.
const BAD_FILENAME_CHARS = /[\\/:*?"<>|\x00-\x1f]/g

function str(v) {
  return typeof v === 'string' ? v.trim() : ''
}

// An asset name is free text; a filename is not. Strip what a file system (or
// a Content-Disposition) would choke on rather than refusing the download.
function safeFileName(s) {
  return s.replace(BAD_FILENAME_CHARS, "_").replace(/\s+/g, " ").trim()
}

function extensionOf(s) {
  const m = /\.[A-Za-z0-9]{1,8}$/.exec(str(s))
  return m ? m[0] : ''
}

function looksLikeFileName(s) {
  // A dotted tail of 1-8 chars: ".npy", ".onnx", ".glb", ".json". A UUID has no
  // dot; a versioned name like "geom.v2.onnx" still ends in a real extension.
  return !!s && !s.includes('/') && /\.[A-Za-z0-9]{1,8}$/.test(s)
}

function lastPathSegment(url) {
  const s = str(url)
  if (!s) return ''
  const noQuery = s.split(/[?#]/)[0]
  const seg = noQuery.split('/').filter(Boolean).pop() ?? ''
  try { return decodeURIComponent(seg) } catch { return seg }
}
